import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Mail, Loader2, RefreshCw, AlertOctagon, TrendingUp, Search, Inbox, Send, Megaphone, ExternalLink, UserPlus, Wand2 } from "lucide-react";
import ClientSendGridSection from "@/components/ClientSendGridSection";
import { CreateSubuserDialog, DomainAuthWizard } from "@/components/sendgrid/SendGridDialogs";

// Module-scope chart-config refs — avoid reallocating on every render.
const SG_BAR_MARGIN = { top: 5, right: 10, left: 0, bottom: 5 };
const SG_AXIS_TICK = { fontSize: 11, fill: "#71717A" };
const SG_LEGEND_STYLE = { fontSize: 12 };
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const SOURCE_LABELS = {
  client_own: "Client's own SendGrid account",
  client_subuser: "Master · subuser",
  iceberg_subuser: "Master · Iceberg fallback",
  env_fallback: "Env fallback (legacy)",
  unconfigured: "Not configured",
};

const PRESETS = [
  { label: "This month", build: () => {
      const t = new Date(); const s = new Date(t.getFullYear(), t.getMonth(), 1);
      return [s.toISOString().slice(0, 10), t.toISOString().slice(0, 10)]; } },
  { label: "Last 30d", build: () => {
      const t = new Date(); const s = new Date(t.getTime() - 29 * 86400000);
      return [s.toISOString().slice(0, 10), t.toISOString().slice(0, 10)]; } },
  { label: "Last 90d", build: () => {
      const t = new Date(); const s = new Date(t.getTime() - 89 * 86400000);
      return [s.toISOString().slice(0, 10), t.toISOString().slice(0, 10)]; } },
  { label: "Last month", build: () => {
      const t = new Date(); const s = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const e = new Date(t.getFullYear(), t.getMonth(), 0);
      return [s.toISOString().slice(0, 10), e.toISOString().slice(0, 10)]; } },
];

export default function ClientSendGridPage() {
  const { clientId } = useParams();
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  // Load basic client info once — we use company_name to prefill the
  // create-subuser dialog, and the primary business_unit website to
  // pre-fill the domain-auth wizard.
  useEffect(() => {
    api.get(`/clients/${clientId}`)
      .then(({ data }) => setClient(data))
      .catch(() => {});
  }, [clientId]);

  const clientWebsiteHost = (() => {
    const bu = ((client?.business_units || [])[0] || {});
    const url = bu.website_url || client?.website_url || "";
    try {
      return new URL(url.startsWith("http") ? url : `https://${url}`)
        .hostname.replace(/^www\./, "");
    } catch { return ""; }
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/clients/${clientId}/sendgrid/stats?start_date=${start}&end_date=${end}`
      );
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load stats");
    } finally { setLoading(false); }
  }, [clientId, start, end]);
  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const deliveryRate = totals.requests
    ? (totals.delivered / totals.requests * 100).toFixed(1) + "%" : "—";
  const openRate = totals.delivered
    ? (totals.unique_opens / totals.delivered * 100).toFixed(1) + "%" : "—";
  const clickRate = totals.delivered
    ? (totals.unique_clicks / totals.delivered * 100).toFixed(1) + "%" : "—";

  return (
    <div className="space-y-6" data-testid="client-sendgrid-page">
      <div>
        <div className="label-overline flex items-center gap-1 mb-1">
          <Mail className="h-3 w-3" /> Communications
        </div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight">SendGrid</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Daily email volume and engagement for this client&apos;s assigned SendGrid sender.
        </p>
      </div>

      {/* Source banner */}
      {data && (
        <div className="flex items-center justify-between rounded-sm border border-zinc-200 bg-zinc-50/60 px-4 py-2.5 flex-wrap gap-2">
          <div className="text-[12px] font-mono text-zinc-700">
            Source: <strong>{SOURCE_LABELS[data.source] || data.source}</strong>
            {data.on_behalf_of && <> · subuser <code>{data.on_behalf_of}</code></>}
            {data.from_email && <> · from <code>{data.from_email}</code></>}
          </div>
          {data.reason && (
            <Badge className="rounded-sm bg-amber-100 text-amber-800 border-amber-200">
              <AlertOctagon className="h-3 w-3 mr-1" /> {data.reason}
            </Badge>
          )}
        </div>
      )}

      {/* Setup card — Create Subuser + Domain Auth Wizard.
       * Onboards a new client into SendGrid without ever leaving Iceberg:
       *  - "Create subuser" spins up a fresh subuser under our master
       *    account and links it to this client
       *  - "Domain auth wizard" walks through SPF/DKIM CNAMEs + link
       *    branding + DMARC guidance so mail from this client's domain
       *    doesn't get spam-flagged. */}
      <Card className="rounded-sm border-zinc-200 bg-gradient-to-br from-blue-50/40 to-white">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="label-overline">Client SendGrid setup</div>
            <div className="text-[12px] text-zinc-600 mt-1 max-w-2xl">
              {data?.on_behalf_of
                ? <>This client sends via subuser <code>{data.on_behalf_of}</code>. Use the wizard to authenticate a domain (e.g. <b>{clientWebsiteHost || "their-domain.com"}</b>) so emails don&apos;t land in spam.</>
                : <>No dedicated SendGrid subuser is linked to this client yet. Create one to isolate their sending reputation and quota, then authenticate their domain.</>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!data?.on_behalf_of && (
              <Button size="sm" onClick={() => setShowCreate(true)}
                className="rounded-sm bg-[#002FA7] hover:bg-[#001F7A]"
                data-testid="sg-client-create-subuser">
                <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Create subuser
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setShowWizard(true)}
              className="rounded-sm"
              data-testid="sg-client-domain-wizard">
              <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Domain auth wizard
            </Button>
          </div>
        </CardContent>
      </Card>

      <CreateSubuserDialog open={showCreate} onOpenChange={setShowCreate}
        clients={client ? [client] : []}
        defaultClientId={clientId}
        onCreated={() => { setShowCreate(false); load(); }} />
      <DomainAuthWizard open={showWizard} onOpenChange={setShowWizard}
        defaultDomain={clientWebsiteHost}
        subuser={data?.on_behalf_of || ""}
        clientId={clientId}
        onComplete={() => { toast.success("Setup complete — verify DNS as your DNS admin publishes each record"); }} />

      <SendCampaignCard clientId={clientId} fromEmail={data?.from_email} />
      <CampaignHistoryCard clientId={clientId} />

      {/* Date range selector */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-4 flex items-end gap-3 flex-wrap">
          <div>
            <Label className="label-overline mb-1.5">Start</Label>
            <Input type="date" value={start}
              onChange={(e) => setStart(e.target.value)}
              className="rounded-sm font-mono w-40"
              data-testid="sg-stats-start" />
          </div>
          <div>
            <Label className="label-overline mb-1.5">End</Label>
            <Input type="date" value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="rounded-sm font-mono w-40"
              data-testid="sg-stats-end" />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {PRESETS.map((p) => (
              <Button key={p.label} variant="outline" size="sm"
                className="rounded-sm h-9 text-[12px]"
                onClick={() => { const [s, e] = p.build(); setStart(s); setEnd(e); }}
                data-testid={`sg-preset-${p.label.replace(/\s/g, "-").toLowerCase()}`}>
                {p.label}
              </Button>
            ))}
          </div>
          <Button onClick={load} disabled={loading}
            className="rounded-sm bg-[#002FA7] text-white hover:bg-[#00227A]"
            data-testid="sg-stats-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Refresh
          </Button>
        </CardContent>
      </Card>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <KPI label="Sent" value={totals.requests} />
        <KPI label="Delivered" value={totals.delivered} sub={deliveryRate} />
        <KPI label="Opens (u)" value={totals.unique_opens} sub={`${totals.opens || 0} total`} />
        <KPI label="Open rate" value={openRate} mono />
        <KPI label="Clicks (u)" value={totals.unique_clicks} sub={`${totals.clicks || 0} total`} />
        <KPI label="Click rate" value={clickRate} mono />
        <KPI label="Bounces" value={totals.bounces} />
        <KPI label="Spam / Unsub" value={(totals.spam_reports || 0) + (totals.unsubscribes || 0)}
          sub={`${totals.spam_reports || 0} sp · ${totals.unsubscribes || 0} un`} />
      </div>

      {/* Daily chart */}
      <Card className="rounded-sm border-zinc-200" data-testid="sg-daily-chart">
        <CardContent className="p-5">
          <div className="label-overline mb-3 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> Daily email volume
          </div>
          {loading ? (
            <div className="py-12 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !data?.rows?.length ? (
            <div className="py-12 text-center text-sm text-zinc-400">
              No SendGrid data in this date range.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.rows} margin={SG_BAR_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F5" />
                <XAxis dataKey="date" tick={SG_AXIS_TICK} />
                <YAxis tick={SG_AXIS_TICK} />
                <Tooltip />
                <Legend wrapperStyle={SG_LEGEND_STYLE} />
                <Bar dataKey="delivered" stackId="a" fill="#10B981" name="Delivered" />
                <Bar dataKey="bounces" stackId="a" fill="#F59E0B" name="Bounces" />
                <Bar dataKey="blocks" stackId="a" fill="#EF4444" name="Blocks" />
                <Bar dataKey="deferred" stackId="a" fill="#8B5CF6" name="Deferred" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Daily table */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden">
        <CardContent className="p-0">
          <div className="px-5 py-3 bg-zinc-50/60 border-b border-zinc-100">
            <div className="label-overline">Daily breakdown</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/60 border-b border-zinc-100">
                <tr className="text-left">
                  <th className="px-3 py-2 label-overline">Date</th>
                  <th className="px-3 py-2 label-overline text-right">Sent</th>
                  <th className="px-3 py-2 label-overline text-right">Delivered</th>
                  <th className="px-3 py-2 label-overline text-right">Opens (u)</th>
                  <th className="px-3 py-2 label-overline text-right">Clicks (u)</th>
                  <th className="px-3 py-2 label-overline text-right">Bounces</th>
                  <th className="px-3 py-2 label-overline text-right">Spam</th>
                  <th className="px-3 py-2 label-overline text-right">Unsub</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((r) => (
                  <tr key={r.date} className="border-b border-zinc-50"
                    data-testid={`sg-daily-row-${r.date}`}>
                    <td className="px-3 py-1.5 font-mono text-[12px]">{r.date}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[12px]">{(r.requests || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[12px] text-emerald-700">{(r.delivered || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[12px]">{(r.unique_opens || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[12px]">{(r.unique_clicks || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[12px] text-amber-700">{(r.bounces || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[12px] text-red-700">{(r.spam_reports || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[12px]">{(r.unsubscribes || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Sender config (re-uses the existing settings panel) */}
      <ClientSendGridSection clientId={clientId} />

      {/* Recent sends panel */}
      <RecentSendsPanel clientId={clientId} />
    </div>
  );
}

const RECENT_PRESETS = [
  { label: "24h", days: 1 },
  { label: "3d", days: 3 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
];

const STATUS_STYLES = {
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  processed: "bg-sky-100 text-sky-800 border-sky-200",
  bounce: "bg-amber-100 text-amber-800 border-amber-200",
  blocked: "bg-rose-100 text-rose-800 border-rose-200",
  block: "bg-rose-100 text-rose-800 border-rose-200",
  deferred: "bg-violet-100 text-violet-800 border-violet-200",
  not_delivered: "bg-zinc-100 text-zinc-800 border-zinc-200",
  spam_report: "bg-red-100 text-red-800 border-red-200",
};

function RecentSendsPanel({ clientId }) {
  const [days, setDays] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testSending, setTestSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: "100",
        since_days: String(days),
      });
      if (search) params.set("search", search);
      const { data } = await api.get(
        `/clients/${clientId}/sendgrid/messages?${params.toString()}`
      );
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load recent sends");
      setData({ available: false, reason: "request failed", messages: [] });
    } finally {
      setLoading(false);
    }
  }, [clientId, days, search]);

  useEffect(() => { load(); }, [load]);

  const sendTestEmail = async () => {
    const to = (testTo || "").trim();
    if (!to) { toast.error("Enter a recipient email"); return; }
    setTestSending(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/sendgrid/test-email`, { to_email: to });
      if (data.sent) {
        toast.success(`Test email sent to ${to} via ${data.on_behalf_of || data.source}`);
        setTestOpen(false);
        setTimeout(load, 3000);
      } else {
        toast.error(data.reason || "Test email failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test email");
    } finally {
      setTestSending(false);
    }
  };

  const messages = data?.messages || [];

  return (
    <Card className="rounded-sm border-zinc-200 overflow-hidden" data-testid="sg-recent-sends-panel">
      <CardContent className="p-0">
        <div className="px-5 py-3 bg-zinc-50/60 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Inbox className="h-3.5 w-3.5 text-zinc-500" />
            <div className="label-overline">Recent sends</div>
            {data && (
              <span className="text-[11px] font-mono text-zinc-500">
                {data.available === false
                  ? "(activity history not available)"
                  : `(${messages.length} message${messages.length === 1 ? "" : "s"})`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {RECENT_PRESETS.map((p) => (
              <Button
                key={p.label}
                variant={days === p.days ? "default" : "outline"}
                size="sm"
                className={`rounded-sm h-8 text-[11px] ${
                  days === p.days ? "bg-[#002FA7] text-white hover:bg-[#00227A]" : ""
                }`}
                onClick={() => setDays(p.days)}
                data-testid={`sg-recent-preset-${p.label}`}
              >
                {p.label}
              </Button>
            ))}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchInput); }}
                placeholder="search email or subject…"
                className="rounded-sm h-8 pl-7 text-[12px] w-56 font-mono"
                data-testid="sg-recent-search"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm h-8"
              onClick={() => setSearch(searchInput)}
              data-testid="sg-recent-search-apply"
            >
              Apply
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm h-8"
              onClick={load}
              disabled={loading}
              data-testid="sg-recent-refresh"
            >
              {loading
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <RefreshCw className="h-3 w-3" />}
            </Button>
            <Button
              size="sm"
              className="rounded-sm h-8 bg-[#002FA7] text-white hover:bg-[#00227A]"
              onClick={() => setTestOpen(true)}
              data-testid="sg-test-email-open"
            >
              <Send className="h-3 w-3 mr-1.5" /> Test send
            </Button>
          </div>
        </div>

        {data?.available === false && (
          <div className="px-5 py-3 bg-amber-50/60 border-b border-amber-100 text-[12px] text-amber-900 flex items-center gap-2">
            <AlertOctagon className="h-3.5 w-3.5" />
            <span>{data.reason || "Email Activity History add-on not enabled on this SendGrid account."}</span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/60 border-b border-zinc-100">
              <tr className="text-left">
                <th className="px-3 py-2 label-overline">Time (UTC)</th>
                <th className="px-3 py-2 label-overline">Status</th>
                <th className="px-3 py-2 label-overline">From</th>
                <th className="px-3 py-2 label-overline">To</th>
                <th className="px-3 py-2 label-overline">Subject</th>
                <th className="px-3 py-2 label-overline text-right">Opens</th>
                <th className="px-3 py-2 label-overline text-right">Clicks</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-zinc-500">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…
                </td></tr>
              )}
              {!loading && messages.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-zinc-400">
                  {data?.available === false
                    ? "—"
                    : `No sends in the last ${days === 1 ? "24 hours" : `${days} days`}.`}
                </td></tr>
              )}
              {!loading && messages.map((m) => {
                const styles = STATUS_STYLES[m.status] || "bg-zinc-100 text-zinc-700 border-zinc-200";
                return (
                  <tr key={m.msg_id || `${m.last_event_time}-${m.to_email}`}
                      className="border-b border-zinc-50"
                      data-testid={`sg-recent-row-${m.msg_id || m.last_event_time}`}>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-600 whitespace-nowrap">
                      {(m.last_event_time || "").replace("T", " ").replace("Z", "")}
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge className={`rounded-sm text-[10px] font-mono uppercase ${styles}`}>
                        {m.status || "—"}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-600 truncate max-w-[200px]" title={m.from_email}>
                      {m.from_email || "—"}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-800 truncate max-w-[220px]" title={m.to_email}>
                      {m.to_email || "—"}
                    </td>
                    <td className="px-3 py-1.5 text-[12px] truncate max-w-[340px]" title={m.subject}>
                      {m.subject || "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-[11px]">{m.opens_count || 0}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[11px]">{m.clicks_count || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="sg-test-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Send a test email</DialogTitle>
            <DialogDescription className="text-[12px] text-zinc-500">
              Fires a small HTML message via this client&apos;s assigned SendGrid identity so you can confirm the wiring end-to-end.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="label-overline">Recipient</Label>
            <Input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="you@example.com"
              className="rounded-sm font-mono"
              data-testid="sg-test-email-to"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm"
              onClick={() => setTestOpen(false)}
              data-testid="sg-test-email-cancel">
              Cancel
            </Button>
            <Button
              className="rounded-sm bg-[#002FA7] text-white hover:bg-[#00227A]"
              onClick={sendTestEmail}
              disabled={testSending}
              data-testid="sg-test-email-send"
            >
              {testSending
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…</>
                : <><Send className="h-3.5 w-3.5 mr-1.5" /> Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function KPI({ label, value, sub, mono }) {
  return (
    <div className="rounded-sm border border-zinc-200 bg-zinc-50/40 px-2.5 py-2">
      <div className="label-overline">{label}</div>
      <div className={`font-display font-bold text-lg mt-0.5 ${mono ? "font-mono" : ""}`}>
        {typeof value === "number" ? value.toLocaleString() : (value ?? "—")}
      </div>
      {sub && <div className="text-[10px] font-mono text-zinc-500">{sub}</div>}
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────────
 * Send Campaign — pick a SendGrid Dynamic Template, paste recipients,
 * fire POST /v3/mail/send via our backend.
 * ────────────────────────────────────────────────────────────────── */
function SendCampaignCard({ clientId, fromEmail }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-4 flex items-center gap-3 flex-wrap justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-9 w-9 rounded-sm grid place-items-center bg-[#1A82E2]/10 shrink-0">
            <Megaphone className="h-4 w-4 text-[#1A82E2]" />
          </span>
          <div className="min-w-0">
            <div className="font-display font-bold text-sm">Send a campaign</div>
            <div className="text-[11px] text-zinc-500 truncate">
              Pick a SendGrid Dynamic Template + paste recipients
              {fromEmail && <> · sent from <code className="font-mono">{fromEmail}</code></>}
            </div>
          </div>
        </div>
        <Button onClick={() => setOpen(true)}
          className="rounded-sm bg-[#1A82E2] hover:bg-[#1573CC] text-white"
          disabled={!fromEmail}
          data-testid="open-send-campaign">
          <Send className="h-4 w-4 mr-1.5" /> New campaign
        </Button>
      </CardContent>
      {open && (
        <SendCampaignDialog clientId={clientId} fromEmail={fromEmail}
          onClose={() => setOpen(false)} />
      )}
    </Card>
  );
}

function SendCampaignDialog({ clientId, fromEmail, onClose }) {
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [recipientsText, setRecipientsText] = useState("");
  const [dataJson, setDataJson] = useState("");
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  // Stored client contacts — lets the admin one-click pick from existing
  // CRM records instead of pasting addresses. Falls back to free-form
  // paste when the client has no contacts on file.
  const [contacts, setContacts] = useState(null);

  useEffect(() => {
    api.get("/sendgrid/templates")
      .then((r) => setTemplates(r.data?.templates || []))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Couldn't load templates");
        setTemplates([]);
      });
    api.get(`/clients/${clientId}/contacts`)
      .then((r) => setContacts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setContacts([]));
  }, [clientId]);

  const addContacts = (selectedEmails) => {
    if (!selectedEmails?.length) return;
    const existing = new Set(
      recipientsText.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean));
    const toAdd = selectedEmails.filter((e) => !existing.has(e.toLowerCase()));
    if (!toAdd.length) {
      toast.info("Those addresses are already in the list");
      return;
    }
    setRecipientsText((prev) => {
      const sep = prev.trim() ? "\n" : "";
      return prev + sep + toAdd.join("\n");
    });
    toast.success(`Added ${toAdd.length} contact${toAdd.length === 1 ? "" : "s"}`);
  };

  const recipients = recipientsText
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const parsedData = (() => {
    if (!dataJson.trim()) return null;
    try { return JSON.parse(dataJson); }
    catch { return "invalid"; }
  })();

  const payload = {
    template_id: templateId,
    recipients,
    subject: subject || undefined,
    from_name: fromName || undefined,
    reply_to: replyTo || undefined,
    dynamic_template_data: parsedData === "invalid" ? undefined : parsedData,
  };

  const doSend = async (testOnly) => {
    if (!templateId) { toast.error("Pick a template first"); return; }
    if (recipients.length === 0) {
      toast.error("Paste at least one recipient email"); return;
    }
    if (parsedData === "invalid") {
      toast.error("Personalisation JSON is not valid"); return;
    }
    setSending(true);
    setPreview(null);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/email-campaigns/send`,
        { ...payload, test_only: testOnly });
      if (testOnly) {
        setPreview(data);
        toast.success(`Preview ready — would send to ${data.would_send_to}`);
      } else {
        toast.success(`Campaign sent to ${data.recipient_count} recipient(s)`);
        onClose();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight">
            Send email campaign
          </DialogTitle>
          <DialogDescription>
            Sent from <code className="font-mono">{fromEmail}</code> using SendGrid
            Dynamic Templates. Up to 1,000 recipients per call.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="label-overline">Template</Label>
            {templates === null ? (
              <div className="h-10 flex items-center gap-2 text-[11px] text-zinc-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading templates…
              </div>
            ) : templates.length === 0 ? (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2">
                No Dynamic Templates on this account. Create one at{" "}
                <a className="underline" target="_blank" rel="noreferrer"
                  href="https://mc.sendgrid.com/dynamic-templates">
                  mc.sendgrid.com <ExternalLink className="h-2.5 w-2.5 inline" />
                </a>{" "}then refresh.
              </div>
            ) : (
              <select value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="mt-1 w-full h-10 rounded-sm border border-zinc-200 bg-white px-2 text-xs font-mono"
                data-testid="campaign-template-select">
                <option value="">— pick a template —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name || "(unnamed)"} · {t.id}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-overline">Subject (optional)</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                className="mt-1 rounded-sm h-9 text-xs"
                placeholder="Overrides the template subject if set"
                data-testid="campaign-subject" />
            </div>
            <div>
              <Label className="label-overline">From name</Label>
              <Input value={fromName} onChange={(e) => setFromName(e.target.value)}
                className="mt-1 rounded-sm h-9 text-xs"
                placeholder="e.g. GBE Group" />
            </div>
          </div>

          <div>
            <Label className="label-overline">Reply-to (optional)</Label>
            <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)}
              className="mt-1 rounded-sm h-9 text-xs font-mono"
              placeholder="hello@gbegroup.com.au" />
          </div>

          <div>
            <Label className="label-overline flex items-center justify-between">
              <span>Recipients</span>
              <span className="text-[10px] font-mono text-zinc-500 normal-case tracking-normal">
                {recipients.length} address(es)
              </span>
            </Label>
            <ContactPicker contacts={contacts} onAdd={addContacts} />
            <textarea value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              className="mt-1 w-full h-24 rounded-sm border border-zinc-200 bg-white p-2 text-xs font-mono resize-y"
              placeholder="alice@example.com&#10;bob@example.com&#10;…paste up to 1,000 emails"
              data-testid="campaign-recipients" />
          </div>

          <div>
            <Label className="label-overline">Personalisation (optional, JSON)</Label>
            <textarea value={dataJson}
              onChange={(e) => setDataJson(e.target.value)}
              className={`mt-1 w-full h-20 rounded-sm border bg-white p-2 text-xs font-mono resize-y ${
                parsedData === "invalid"
                  ? "border-red-300" : "border-zinc-200"}`}
              placeholder='{"first_name":"Friend","company":"GBE Group"}'
              data-testid="campaign-personalisation" />
            <div className="text-[10px] font-mono text-zinc-500 mt-1">
              Applied as <code>dynamic_template_data</code> to every recipient.
              Handlebars <code>&#123;&#123;first_name&#125;&#125;</code> placeholders in the
              template will resolve to these values.
              {parsedData === "invalid" && (
                <span className="text-red-600 ml-2">· not valid JSON</span>
              )}
            </div>
          </div>

          {preview && (
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-2 text-[11px] font-mono space-y-1">
              <div><strong>Test preview</strong> · would send to {preview.would_send_to}</div>
              <details>
                <summary className="cursor-pointer">Payload (first 3 personalisations)</summary>
                <pre className="whitespace-pre-wrap break-all mt-1">
                  {JSON.stringify(preview.preview_payload, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-sm" onClick={onClose}
            disabled={sending}>
            Cancel
          </Button>
          <Button variant="outline" className="rounded-sm"
            onClick={() => doSend(true)} disabled={sending}
            data-testid="campaign-preview">
            {sending && preview === null
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              : <Search className="h-3.5 w-3.5 mr-1.5" />}
            Preview
          </Button>
          <Button className="rounded-sm bg-[#1A82E2] hover:bg-[#1573CC] text-white"
            onClick={() => doSend(false)} disabled={sending}
            data-testid="campaign-send">
            {sending && preview !== null
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Send to {recipients.length}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignHistoryCard({ clientId }) {
  const [rows, setRows] = useState(null);
  const refresh = useCallback(() => {
    api.get(`/clients/${clientId}/email-campaigns`)
      .then((r) => setRows(r.data?.campaigns || []))
      .catch(() => setRows([]));
  }, [clientId]);
  useEffect(() => { refresh(); }, [refresh]);
  if (rows === null || rows.length === 0) return null;
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="campaign-history-card">
      <CardContent className="p-0">
        <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
          <div className="font-display font-bold text-sm flex items-center gap-1.5">
            <Megaphone className="h-3.5 w-3.5" />
            Recent campaigns ({rows.length})
          </div>
          <button onClick={refresh}
            className="text-[10px] font-mono text-zinc-500 hover:text-zinc-900">
            refresh
          </button>
        </div>
        <div className="divide-y divide-zinc-100">
          {rows.map((r) => (
            <div key={r.campaign_id}
              className="px-4 py-2 grid grid-cols-[140px_120px_1fr_70px] gap-3 items-center text-[11px]"
              data-testid={`campaign-row-${r.campaign_id}`}>
              <span className="font-mono text-zinc-500">
                {String(r.sent_at).slice(0, 19).replace("T", " ")}
              </span>
              <span className="font-mono text-zinc-700 truncate" title={r.template_id}>
                {r.template_id}
              </span>
              <span className="truncate">
                {r.subject || <em className="text-zinc-400">(template subject)</em>}
              </span>
              <span className="font-mono text-zinc-700 text-right">
                {r.recipient_count} sent
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}



/**
 * Compact contact-picker for the campaign Recipients field.
 *
 * Renders a "Pick from contacts" button → popover with a checkbox list of the
 * client's stored contacts (primary first, then alphabetised). Selected
 * emails are appended to the parent's recipients textarea via `onAdd` so the
 * admin doesn't have to copy-paste the same addresses every send.
 */
function ContactPicker({ contacts, onAdd }) {
  const [open, setOpen] = React.useState(false);
  const [picked, setPicked] = React.useState(() => new Set());
  const list = React.useMemo(() => {
    const rows = (contacts || []).filter((c) => c.email);
    return rows.sort((a, b) => {
      if ((b.is_primary ? 1 : 0) !== (a.is_primary ? 1 : 0)) return (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0);
      return (a.name || a.email || "").localeCompare(b.name || b.email || "");
    });
  }, [contacts]);
  if (contacts === null) {
    return <div className="text-[10px] text-zinc-400 mt-1 mb-1">Loading saved contacts…</div>;
  }
  if (!list.length) {
    return <div className="text-[10px] text-zinc-400 mt-1 mb-1">No saved client contacts — paste emails manually below.</div>;
  }
  const toggle = (email) => {
    const next = new Set(picked);
    if (next.has(email)) next.delete(email); else next.add(email);
    setPicked(next);
  };
  return (
    <div className="mt-1 mb-1">
      <button type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5"
        data-testid="contact-picker-toggle">
        {open ? "Hide" : "Pick from contacts"} ({list.length})
      </button>
      {open && (
        <div className="mt-1.5 rounded-sm border border-zinc-200 bg-white p-2 max-h-44 overflow-y-auto space-y-1"
          data-testid="contact-picker-panel">
          {list.map((c) => (
            <label key={c.user_id}
              className="flex items-center gap-2 text-[11px] cursor-pointer hover:bg-zinc-50 px-1.5 py-0.5 rounded-sm">
              <input type="checkbox"
                checked={picked.has(c.email)}
                onChange={() => toggle(c.email)}
                className="h-3 w-3 accent-[#002FA7]"
                data-testid={`contact-pick-${c.user_id}`} />
              <span className="font-mono">{c.email}</span>
              {c.name && <span className="text-zinc-500">· {c.name}</span>}
              {c.is_primary && (
                <span className="ml-auto text-[9px] uppercase font-bold text-emerald-700">Primary</span>
              )}
            </label>
          ))}
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
            <button type="button"
              onClick={() => { setPicked(new Set()); setOpen(false); }}
              className="text-[10px] text-zinc-500 hover:text-zinc-700">Cancel</button>
            <button type="button"
              onClick={() => {
                onAdd(Array.from(picked));
                setPicked(new Set());
                setOpen(false);
              }}
              disabled={!picked.size}
              className="text-[10px] px-2 py-0.5 rounded-sm bg-[#002FA7] text-white disabled:opacity-50"
              data-testid="contact-picker-add">
              Add {picked.size || ""}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
